import { Check, Pencil, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { useState } from 'react';

export function ShoppingListItem({ 
  id, 
  name, 
  quantity, 
  unit,
  checked, 
  onToggle, 
  onEdit, 
  onMove, 
  isReorderMode, 
  index = 0, 
  totalItems,
  onDragStart,
  onDrop,
  categoryColor = 'var(--muted-foreground)', // Neutral accent bar
  categoryName,
  isFirstInCategory = false,
  isLastInCategory = false
}) {
  const [dropPosition, setDropPosition] = useState(null);
  const displayQuantity = (() => {
    // No number (e.g. "parsley, a handful"): show nothing rather than a stray unit or 0.
    if (quantity === '' || quantity == null) return '';
    const u = typeof unit === 'string' ? unit.trim() : '';
    if (!u || u.toLowerCase() === 'unit') return quantity;
    return `${quantity} ${u}`;
  })();
  
  const handleDragOver = (e) => {
    if (!isReorderMode) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Determine if we're in the top or bottom half of the item
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'top' : 'bottom';
    setDropPosition(position);
  };
  
  const handleDragLeave = () => {
    setDropPosition(null);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onDrop) {
      // If dropping on top half, use current index. If bottom half, use next index
      const targetIndex = dropPosition === 'top' ? index : index + 1;
      onDrop(e, targetIndex);
    }
    setDropPosition(null);
  };
  
  return (
    <div 
      className={`flex items-center gap-3 py-3 px-4 bg-card rounded-xl transition-all duration-300 relative overflow-hidden ${
        dropPosition === 'top' ? 'border-t-2 border-[var(--accent)]' : ''
      } ${
        dropPosition === 'bottom' ? 'border-b-2 border-[var(--accent)]' : ''
      } ${
        checked ? 'opacity-60' : 'opacity-100'
      }`}
      draggable={isReorderMode}
      onDragStart={(e) => onDragStart && onDragStart(e, { id, name, quantity, checked })}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Category Color Accent Bar */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
        style={{ backgroundColor: categoryColor }}
      />
      
      {isReorderMode ? (
        <>
          {/* Drag Handle */}
          <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 text-gray-400 cursor-grab active:cursor-grabbing">
            <GripVertical className="w-5 h-5" />
          </div>
          
          {/* Item Content - dimmed checkbox */}
          <div 
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all opacity-40 ${
              checked 
                ? 'bg-primary border-primary' 
                : 'border-[var(--border)]'
            }`}
          >
            {checked && <Check className="w-4 h-4 text-white" />}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className={`transition-all ${
                checked 
                  ? 'line-through text-[var(--muted-foreground)]' 
                  : 'text-foreground'
              }`}>
                {name}
              </span>
              <span className={`text-sm text-[var(--muted-foreground)] flex-shrink-0 ${checked ? 'line-through' : ''}`}>
                {displayQuantity}
              </span>
            </div>
          </div>
          
          {/* Up/Down Buttons */}
          <div className="flex flex-col gap-0.5 flex-shrink-0">
            <button
              onClick={() => onMove && onMove(id, 'up')}
              disabled={false}
              className="w-8 h-6 flex items-center justify-center rounded hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Move ${name} up`}
            >
              <ChevronUp className="w-4 h-4 text-[var(--primary)]" />
            </button>
            <button
              onClick={() => onMove && onMove(id, 'down')}
              disabled={false}
              className="w-8 h-6 flex items-center justify-center rounded hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={`Move ${name} down`}
            >
              <ChevronDown className="w-4 h-4 text-[var(--primary)]" />
            </button>
          </div>
        </>
      ) : (
        <>
          <div 
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
              checked 
                ? 'bg-primary border-primary' 
                : 'border-[var(--border)] hover:border-[color-mix(in srgb,var(--border) 60%, var(--foreground) 40%)]'
            }`}
            onClick={() => onToggle(id)}
            role="checkbox"
            aria-checked={!!checked}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onToggle(id);
              }
            }}
          >
            {checked && <Check className="w-4 h-4 text-white" />}
          </div>
          
          <div className="flex-1 min-w-0" onClick={() => onToggle(id)}>
            <div className="flex items-baseline justify-between gap-2">
              <span className={`transition-all cursor-pointer ${
                checked 
                  ? 'line-through text-[var(--muted-foreground)]' 
                  : 'text-foreground'
              }`}>
                {name}
              </span>
              <span className={`text-sm text-[var(--muted-foreground)] flex-shrink-0 ${checked ? 'line-through' : ''}`}>
                {displayQuantity}
              </span>
            </div>
          </div>
          
          <button
            onClick={() => onEdit(id)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
            aria-label={`Edit ${name}`}
          >
            <Pencil className="w-4 h-4 text-[var(--primary)]" />
          </button>
        </>
      )}
    </div>
  );
}
